import React, { useEffect, useMemo, useState } from 'react'
import { Grid, Row, Column } from '@ublique/components/Grid'
import Dropdown from '@ublique/components/Dropdown'
import Button from '@ublique/components/Button'
import { TextArea } from '@ublique/components/TextInput'
import { isEmpty, isNull } from 'lodash'
import {
  CONDITION,
  KEY,
  OPERATOR,
  SUBCONDITION,
  VALUE,
  conditions,
  defaultGeneratorConfig,
  initialRuleState,
  initialState,
  operatorsByFieldMap
} from './constants'
import factoryMap from './factory'
import { buildRule, manageSuperscripts } from './utils'
import useValidate from './hooks/useValidate'

import cn from 'classnames'

import './index.scss'

function RulesGenerator({ config = defaultGeneratorConfig, t, ns, saveRule }) {
  const translate = (label) => t(`${ns}.${label}`)
  const [data, setData] = useState(initialState)
  const [rule, setRule] = useState(initialRuleState)
  const { fields, validationUrl } = config

  const { loading, validateRule, validated, setValidated } = useValidate(
    validationUrl,
    saveRule
  )

  const operatorsByKeyFieldType = useMemo(
    () => operatorsByFieldMap[data.keyField.type] ?? [],
    [data]
  )

  const translatedOperators = useMemo(
    () =>
      operatorsByKeyFieldType.map((op) => ({
        ...op,
        label: translate(op.label)
      })),
    [operatorsByKeyFieldType]
  )

  const isBtnDisabled = (prevBtnValue, value) => !prevBtnValue || value
  const valueFieldComponentProps = {
    className: 'value-field',
    onChange: ({ params, isTriggered, isNumberDate, number }) =>
      onChange(params, VALUE, isTriggered, isNumberDate, number),
    fieldValue: data.valueField,
    items: data.keyField?.options,
    disabled: isBtnDisabled(data.operator.value, data.valueField.value),
    operator: data.operator,
    t: translate
  }

  const onChange = (params, field, isTriggered, isNumberDate, number) => {
    saveRule('')
    const newParams = manageSuperscripts(params) // apici

    buildRule({
      params: newParams,
      isTriggered,
      setRule,
      data,
      isNumberDate,
      number
    })

    setData((prev) => ({
      ...prev,
      [field]: { ...prev[field], ...newParams }
    }))

    if ([CONDITION, SUBCONDITION].includes(field)) {
      setData(initialState)
    }
    setValidated(null)
  }

  const resetAll = () => {
    setData(initialState)
    setRule(initialRuleState)
    setValidated(null)
  }

  const ValueFieldComponent = factoryMap[data.keyField.type]

  return (
    <Grid id='rules-generator'>
      <Row>
        <form>
          <div className='overflow-box'>
            <Row className='row'>
              <Column lg={12}>
                <div className='header-btns'>
                  <div>
                    <Button
                      onClick={() =>
                        onChange(conditions.OPENSUBCONDITION, SUBCONDITION)
                      }
                    >
                      {translate('OPEN_SUB_COND')}
                    </Button>
                    <Button
                      onClick={() =>
                        onChange(conditions.CLOSESUBCONDITION, SUBCONDITION)
                      }
                    >
                      {translate('CLOSE_SUB_COND')}
                    </Button>
                  </div>
                  <Button kind='tertiary' onClick={resetAll}>
                    {translate('CLEAR')}
                  </Button>
                </div>
              </Column>
              <Column md={4} lg={4}>
                <Dropdown
                  className='key-field'
                  titleText='Fields'
                  selectedItem={data.keyField}
                  items={fields}
                  disabled={isBtnDisabled(true, data.keyField.value)}
                  itemToString={(field) => field.label}
                  onChange={({ selectedItem }) => onChange(selectedItem, KEY)}
                />
              </Column>
              <Column md={4} lg={2} className='operator-col'>
                <Dropdown
                  className='operators-dropdown'
                  titleText='Operators'
                  selectedItem={data.operator}
                  items={translatedOperators}
                  disabled={isBtnDisabled(
                    data.keyField.value,
                    data.operator.value
                  )}
                  itemToString={(op) => op.label}
                  onChange={({ selectedItem }) =>
                    onChange(selectedItem, OPERATOR)
                  }
                />
              </Column>
              <Column md={4} lg={4} className='value-col'>
                <ValueFieldComponent {...valueFieldComponentProps} />
              </Column>
              <Column md={4} lg={2} className='conditions-btns'>
                <Button onClick={() => onChange(conditions.AND, CONDITION)}>
                  {conditions.AND.label}
                </Button>
                <Button onClick={() => onChange(conditions.OR, CONDITION)}>
                  {conditions.OR.label}
                </Button>
              </Column>
              <Column lg={12}>
                <div className='rule-area-validate'>
                  <div className={cn('loading-container', { active: loading })}>
                    <TextArea
                      id='rule-text'
                      rows={15}
                      disabled={isEmpty(rule?.label)}
                      readOnly
                      value={rule.label}
                    />
                  </div>
                  <div
                    className={cn('validate', {
                      validated,
                      'not-validated': !isNull(validated) && !validated
                    })}
                    onClick={() => validateRule(rule)}
                  >
                    {translate('VALIDATE')}
                  </div>
                </div>
              </Column>
            </Row>
          </div>
        </form>
      </Row>
    </Grid>
  )
}

export default RulesGenerator
